import type {
  BaseComponentProps,
  ContentfulLinkType,
  ContentfulNavigationType,
} from '@bedrock/core/contentful';
import { Separator } from '@bedrock/ui/separator';
import { SheetClose } from '@bedrock/ui/sheet';
import { cn } from '@bedrock/ui/utils';
import MenuButton from './menu-button';
import { NavigationLink } from './navigation-link';
import { isNavigationType } from './utils';

const DRAWER_BUTTON_BASE =
  'drawer-button justify-between active:hover:pl-sm py-md px-sm active:border-none' +
  ' [&>*:first-child]:motion-safe:transition-all' +
  ' [&>*:first-child]:active:border-l-4 [&>*:first-child]:active:pl-xs sm:[&>*:first-child]:active:pl-sm' +
  ' [&>*:first-child]:active:border-foreground' +
  ' [&>*:first-child]:hover:border-l-4 [&>*:first-child]:hover:pl-2xs sm:[&>*:first-child]:hover:pl-xs' +
  ' [&>*:first-child]:hover:border-transparent text-left';

type FeaturedLinksProps = {
  featuredLinks?: (ContentfulLinkType | ContentfulNavigationType)[];
  isInView: boolean;
} & Pick<BaseComponentProps, 'locale'>;

export default function FeaturedLinks({
  locale,
  featuredLinks,
  isInView,
}: Readonly<FeaturedLinksProps>) {
  const featuredNavigation = featuredLinks?.[0];

  if (!featuredLinks?.length || !isNavigationType(featuredNavigation)) return null;

  return (
    <li className="patterns-navigation-hamburger-featured mt-md gap-md flex flex-col">
      <Separator />
      <div className="gap-sm flex flex-col">
        {featuredNavigation.title ? (
          <h3 className="patterns-navigation-hamburger-featured-heading br-type-title-xl pl-sm text-foreground font-semibold">
            {featuredNavigation.title}
          </h3>
        ) : null}
        <ul className="patterns-navigation-hamburger-featured-items gap-2xs flex flex-col">
          {featuredNavigation.navigation
            .filter((item) => item.sysType === 'link')
            .map((link, index) => (
              // eslint-disable-next-line react/no-array-index-key -- Contentful may return duplicate sysId entries
              <SheetClose key={`${link.sysId}-${index}`} asChild>
                <MenuButton
                  asChild
                  data-is-parent-link={false}
                  className={cn(
                    'patterns-navigation-hamburger-action',
                    DRAWER_BUTTON_BASE,
                    'max-sm:p-sm br-type-functional-md-strong sm:br-type-functional-lg-strong',
                  )}
                >
                  <NavigationLink link={link} locale={locale} tabIndex={isInView ? 0 : -1}>
                    <span>{link.label}</span>
                  </NavigationLink>
                </MenuButton>
              </SheetClose>
            ))}
        </ul>
      </div>
    </li>
  );
}
